package com.tovtalk.domain.entity

/**
 * Domain Result Wrapper - עטיפה לתוצאות (renamed from Result to avoid clash with kotlin.Result)
 */
/** Server error code for 403 kashrut restriction; client shows dialog with link to settings. */
const val ERROR_CODE_KASHRUT_NOT_ALLOWED = "KASHRUT_NOT_ALLOWED"

/** Server returns 403 with detail "user_does_not_accept_messages"; client shows message that user doesn't accept chat. */
const val ERROR_CODE_USER_DOES_NOT_ACCEPT_MESSAGES = "user_does_not_accept_messages"

sealed class DomainResult<out T> {
    data class Success<T>(val data: T) : DomainResult<T>()
    data class Error(
        val message: String,
        val cause: Throwable? = null,
        val errorCode: String? = null
    ) : DomainResult<Nothing>() {
        val isKashrutNotAllowed: Boolean get() = errorCode == ERROR_CODE_KASHRUT_NOT_ALLOWED
        val isUserDoesNotAcceptMessages: Boolean get() = errorCode == ERROR_CODE_USER_DOES_NOT_ACCEPT_MESSAGES
    }
    data object Loading : DomainResult<Nothing>()

    val isSuccess: Boolean
        get() = this is Success

    val isError: Boolean
        get() = this is Error

    val isLoading: Boolean
        get() = this is Loading

    fun getOrNull(): T? = when (this) {
        is Success -> data
        else -> null
    }

    fun getOrThrow(): T = when (this) {
        is Success -> data
        is Error -> throw cause ?: Exception(message)
        is Loading -> throw IllegalStateException("Result is still loading")
    }

    fun getOrElse(default: @UnsafeVariance T): T = when (this) {
        is Success -> data
        else -> default
    }

    inline fun <R> map(transform: (T) -> R): DomainResult<R> = when (this) {
        is Success -> Success(transform(data))
        is Error -> Error(message, cause, errorCode)
        is Loading -> this
    }

    inline fun onSuccess(action: (T) -> Unit): DomainResult<T> {
        if (this is Success) action(data)
        return this
    }

    inline fun onError(action: (String) -> Unit): DomainResult<T> {
        if (this is Error) action(message)
        return this
    }
}

/**
 * Extension functions
 */
suspend fun <T> DomainResult<T>.suspendOnSuccess(action: suspend (T) -> Unit): DomainResult<T> {
    if (this is DomainResult.Success) action(data)
    return this
}

suspend fun <T> DomainResult<T>.suspendOnError(action: suspend (String) -> Unit): DomainResult<T> {
    if (this is DomainResult.Error) action(message)
    return this
}
